import { defineStore } from 'pinia';
import { HubConnectionBuilder, HubConnection, LogLevel, HubConnectionState } from '@microsoft/signalr';
import axios from 'src/api/axiosConfig';

export type NewMessagePayload = {
  eocId: number;
  timestamp: string;
  // SignalR connection ID of the sender. The handler in TheDashboard.vue
  // compares this against the current store's connectionId and skips
  // refetching when they match — that's the sender's own echo.
  senderConnectionId?: string | null;
};
export type NewMessageHandler = (payload: NewMessagePayload | null) => void | Promise<void>;

export type UnreadCountPayload = { eocId: number; unreadCount: number };
export type UnreadCountHandler = (payload: UnreadCountPayload | null) => void | Promise<void>;

export type NewEpisodeOfCareSummaryPayload = { episodeOfCareId: number; createdDate: string };
export type NewEpisodeOfCareSummaryHandler = (payload: NewEpisodeOfCareSummaryPayload | null) => void | Promise<void>;

// Full-restart backoff once a connection has died permanently (the automatic
// reconnect schedule exhausts after ~47s of failures). Jitter spreads out the
// herd of sessions re-negotiating after a service outage. Retries forever.
const RESTART_DELAYS_MS = [5000, 15000, 30000, 60000];
const RESTART_JITTER_MS = 5000;

interface RealtimeState {
  connection: HubConnection | null;
  // SignalR connection ID — captured after start() / refreshed after reconnect.
  // Sent in send-message requests so the server can include it in the
  // newMessage event payload, letting the originating client filter out its
  // own echo while other connections of the same user (other tabs/browsers)
  // process the event normally.
  connectionId: string | null;
  isConnected: boolean;
  starting: Promise<void> | null;
  handlers: NewMessageHandler[];
  unreadCountHandlers: UnreadCountHandler[];
  episodeOfCareSummaryHandlers: NewEpisodeOfCareSummaryHandler[];
  // Set by stop() BEFORE stopping so the onclose handler and any pending
  // restart know the death was intentional and must not resurrect.
  intentionallyStopped: boolean;
  restartAttempt: number;
  restartTimer: number | null;
}

export const useRealtimeStore = defineStore('realtime', {
  state: (): RealtimeState => ({
    connection: null,
    connectionId: null,
    isConnected: false,
    starting: null,
    handlers: [],
    unreadCountHandlers: [],
    episodeOfCareSummaryHandlers: [],
    intentionallyStopped: false,
    restartAttempt: 0,
    restartTimer: null,
  }),
  actions: {
    async start() {
      if (this.connection?.state === HubConnectionState.Connected) return;
      if (this.starting) return this.starting;
      this.intentionallyStopped = false;

      this.starting = this._connect().finally(() => {
        this.starting = null;
      });

      return this.starting;
    },

    // Builds (or rebuilds) the connection: fresh negotiate, new HubConnection,
    // handlers wired, started. Used by start() and by the restart loop.
    async _connect() {
      const { data } = await axios.post('/SignalR/Negotiate', {});
      const result = data?.Result || data;

      // Azure SignalR access tokens expire (~1h). SignalR calls the token
      // factory before every (re)connect attempt — and per request on the
      // SSE/long-polling fallbacks — so we cache the token and re-negotiate
      // only when it goes stale. Per-connection closure: every restart
      // re-negotiates, so this always starts fresh.
      const TOKEN_MAX_AGE_MS = 45 * 60 * 1000; // 1h lifetime minus safety margin
      let cachedToken: string = result.accessToken;
      let tokenFetchedAt = Date.now();
      let tokenRefresh: Promise<string> | null = null;

      const getAccessToken = (): Promise<string> => {
        if (Date.now() - tokenFetchedAt < TOKEN_MAX_AGE_MS) {
          return Promise.resolve(cachedToken);
        }
        if (!tokenRefresh) {
          // Single in-flight refresh shared by concurrent factory calls.
          // A failed refresh clears the slot and rejects; SignalR's retry
          // schedule provides the retry cadence.
          tokenRefresh = axios.post('/SignalR/Negotiate', {})
            .then(({ data: refreshed }) => {
              const r = refreshed?.Result || refreshed;
              cachedToken = r.accessToken;
              tokenFetchedAt = Date.now();
              return cachedToken;
            })
            .finally(() => { tokenRefresh = null; });
        }
        return tokenRefresh;
      };

      const conn = new HubConnectionBuilder()
        .withUrl(result.url, { accessTokenFactory: getAccessToken })
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .configureLogging(LogLevel.Warning)
        .build();

      conn.on('newMessage', (payload: NewMessagePayload) => {
        this.handlers.forEach(h => {
          try { h(payload); } catch (e) { console.warn(e); }
        });
      });

      conn.on('unreadMessageCount', (payload: UnreadCountPayload) => {
        this.unreadCountHandlers.forEach(h => {
          try { h(payload); } catch (e) { console.warn(e); }
        });
      });

      conn.on('newEpisodeOfCareSummary', (payload: NewEpisodeOfCareSummaryPayload) => {
        this.episodeOfCareSummaryHandlers.forEach(h => {
          try { h(payload); } catch (e) { console.warn(e); }
        });
      });

      conn.onreconnected(() => {
        // The connection ID may change after a reconnect — refresh ours so
        // future sends include the right value for echo-filtering.
        this.connectionId = conn.connectionId;
        this.isConnected = true;
        // Synthetic replay so subscribers reconcile after a network gap
        this.handlers.forEach(h => {
          try { h(null); } catch { /* swallow */ }
        });
        this.unreadCountHandlers.forEach(h => {
          try { h(null); } catch { /* swallow */ }
        });
        this.episodeOfCareSummaryHandlers.forEach(h => {
          try { h(null); } catch { /* swallow */ }
        });
      });

      conn.onclose(() => {
        this.isConnected = false;
        // The in-connection reconnect schedule exhausted (~47s of failures) —
        // this connection object is permanently dead. Rebuild from scratch
        // unless the session stopped on purpose.
        if (!this.intentionallyStopped) {
          this._scheduleRestart();
        }
      });

      await conn.start();
      if (this.intentionallyStopped) {
        // stop() happened while this (re)start was in flight — don't
        // install a zombie connection.
        try { await conn.stop(); } catch { /* swallow */ }
        return;
      }
      this.connection = conn;
      this.connectionId = conn.connectionId;
      this.isConnected = true;
      this.restartAttempt = 0;
    },

    _scheduleRestart() {
      if (this.intentionallyStopped || this.restartTimer !== null) return;
      const base = RESTART_DELAYS_MS[Math.min(this.restartAttempt, RESTART_DELAYS_MS.length - 1)];
      const delay = base + Math.floor(Math.random() * RESTART_JITTER_MS);
      this.restartAttempt++;
      this.restartTimer = window.setTimeout(async () => {
        this.restartTimer = null;
        if (this.intentionallyStopped) return;
        try {
          // Go through start(), not _connect() directly: its single-flight
          // `starting` guard and already-connected check prevent a duplicate
          // connection if a component called start() while we were waiting.
          await this.start();
          if (this.intentionallyStopped || !this.isConnected) return;
          // Recovered after a dead window — synthetic replay so subscribers
          // refetch whatever arrived while the connection was down.
          this.handlers.forEach(h => {
            try { h(null); } catch { /* swallow */ }
          });
          this.unreadCountHandlers.forEach(h => {
            try { h(null); } catch { /* swallow */ }
          });
          this.episodeOfCareSummaryHandlers.forEach(h => {
            try { h(null); } catch { /* swallow */ }
          });
        } catch {
          this._scheduleRestart();
        }
      }, delay);
    },

    onNewMessage(handler: NewMessageHandler) {
      this.handlers.push(handler);
      return () => {
        this.handlers = this.handlers.filter(x => x !== handler);
      };
    },

    onUnreadCount(handler: UnreadCountHandler) {
      this.unreadCountHandlers.push(handler);
      return () => {
        this.unreadCountHandlers = this.unreadCountHandlers.filter(x => x !== handler);
      };
    },

    onNewEpisodeOfCareSummary(handler: NewEpisodeOfCareSummaryHandler) {
      this.episodeOfCareSummaryHandlers.push(handler);
      return () => {
        this.episodeOfCareSummaryHandlers = this.episodeOfCareSummaryHandlers.filter(x => x !== handler);
      };
    },

    async stop() {
      this.intentionallyStopped = true;
      if (this.restartTimer !== null) {
        clearTimeout(this.restartTimer);
        this.restartTimer = null;
      }
      const c = this.connection;
      this.connection = null;
      this.connectionId = null;
      this.isConnected = false;
      this.handlers = [];
      this.unreadCountHandlers = [];
      this.episodeOfCareSummaryHandlers = [];
      if (c) await c.stop();
    },
  },
});
